/** Converts "r g b" string to Hex */
export function rgbStringToHex(rgb: string): string {
  const [r, g, b] = rgb.split(' ').map(Number);
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
}

/** Simple Hex to RGB object */
export function hexToRgb(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 };
}

/**
 * Converts HSL values to a space-separated RGB string.
 * @param h - Hue (0-360)
 * @param s - Saturation (0-100)
 * @param l - Lightness (0-100)
 * @returns string - "R G B"
 */
export function hslToRgbString(h: number, s: number, l: number): string {
  h /= 360;
  s /= 100;
  l /= 100;

  let r, g, b;

  if (s === 0) {
    r = g = b = l; // Achromatic (gray)
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return `${Math.round(r * 255)} ${Math.round(g * 255)} ${Math.round(b * 255)}`;
}

/** * Lightens or Darkens a color by a percentage.
 * Positive percent = lighten, Negative percent = darken
 */
export function adjustColor(hex: string, percent: number) {
  const { r, g, b } = hexToRgb(hex);

  const adjust = (val: number) =>
    Math.round(Math.min(255, Math.max(0, val + (percent > 0 ? 255 - val : val) * (percent / 100))));

  return {
    r: adjust(r),
    g: adjust(g),
    b: adjust(b),
  };
}
